import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchFulfillmentsData, loadFulfillmentBatchForPrinting, exportFulfillmentBatchCSV, clearFulfillmentPrintDetails } from './fulfillmentsLogSlice';

// mui components
import {
    Typography,
    FormControl,
    Pagination,
    IconButton,
    Tooltip,
    Card,
    Button,
    Select,
    MenuItem,
    Stack
} from "@mui/material";
import Box from '@mui/material/Box';
import LinearProgress from '@mui/material/LinearProgress';
import FilterAltOutlinedIcon from '@mui/icons-material/FilterAltOutlined';
import LocalPrintshopIcon from '@mui/icons-material/LocalPrintshop';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import AssessmentIcon from '@mui/icons-material/Assessment';
import { DataGrid, GridToolbarContainer } from '@mui/x-data-grid';
import Printer from '../../../pages/General/Printer'
import {
    printBulkLabels, 
} from '../../../pages/Orders/ordersSlice';
// Project Components
import FulfillmentsLogFilters from './FulfillmentsLogFilters';
import NorowsData from '../../../pages/General/TrackingDrawer'; // Fallback mapping match

// Custom focus styles from reference setup
const enterpriseControlInputStyles = {
    '& .MuiOutlinedInput-root': {
        borderRadius: '10px',
        backgroundColor: '#FFFFFF',
        transition: 'all 0.2s ease-in-out',
        border: '1px solid #E2E8F0',
        '& fieldset': { border: 'none' },
        '&:hover': { borderColor: '#CBD5E1' },
        '&.Mui-focused': {
            borderColor: '#1976d2',
            boxShadow: '0 0 0 3px rgba(25, 118, 210, 0.08)'
        }
    },
    '& input': {
        padding: '10px 14px !important',
        fontSize: '13.5px',
        color: '#1E293B',
        fontWeight: 500
    },
    '& .MuiSelect-select': {
        padding: '8px 14px !important',
        fontSize: '13px',
        color: '#1E293B',
        fontWeight: 500
    }
};

const utilityIconStyles = (isActive) => ({
    color: '#1976d2',
    padding: '8px',
    borderRadius: '10px',
    backgroundColor: isActive ? 'rgba(25, 118, 210, 0.08)' : 'transparent',
    border: isActive ? '1px solid rgba(25, 118, 210, 0.2)' : '1px solid #E2E8F0',
    transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
    '&:hover': {
        backgroundColor: isActive ? 'rgba(25, 118, 210, 0.12)' : 'rgba(241, 245, 249, 0.8)',
        borderColor: isActive ? 'rgba(25, 118, 210, 0.3)' : '#CBD5E1',
        transform: 'translateY(-1px)'
    },
    '&:active': { transform: 'translateY(0)' }
});

const FulfillmentsReportPage = () => {
    const dispatch = useDispatch();
    const fulfillments = useSelector(state => state.fulfillmentsLog);
    const orders = useSelector(state => state.orders);
    const profile = useSelector(state => state.auth);
    const currentUserId = profile?.profile?.id || 3;
    const [printUrl, setPrintUrl] = useState("")
    const [isPrinted, setIsPrinted] = useState(true)
    console.log(fulfillments)
    const [showFilter, setShowFilter] = useState(false);
    const [meta, setMeta] = useState({
        page: 1,
        per_page: 10,
        start_date: new Date(new Date().setDate(new Date().getDate() - 10)).toISOString().split('T')[0],
        end_date: new Date().toISOString().split('T')[0],
        sorting: JSON.stringify([{ field: 'log_date', sort: 'desc' }])
    });

    useEffect(() => {
        dispatch(fetchFulfillmentsData(meta));
    }, [dispatch, meta]);

    // Ensure this selector sits at the top of your component alongside your others
    const fulfillmentsPrintState = useSelector(state => state.fulfillmentsLog?.batchPrintDetails || { data: null, loading: false });

    // ─── FULLY UPDATED RELATIONAL SQL USEEFFECT FOR PRINT LOADING & CLEARING ───
    useEffect(() => {
        const details = fulfillmentsPrintState;

        // 1. Check if loading is finished and data exists
        if (!details.loading && details.data) {
            let labels = [];

            // 2. Convert everything to an array to handle single objects or bulk listings consistently
            const shipments = Array.isArray(details.data) ? details.data : [details.data];

            // 3. Loop through all table rows (extracting flat columns from the shipment model schema)
            shipments.forEach((shipmentData) => {
                // Priority extraction matching your SQL table column definitions
                const url = shipmentData?.carrier_label_url || 
                            shipmentData?.label_url || 
                            shipmentData?.direct_label_url ||
                            shipmentData?.detachable_label_url ||
                            shipmentData?.detachable_direct_label_url;

                // Handle SQL tinyint/boolean void checks safely
                const isVoided = shipmentData?.is_voided === 1 || 
                                 shipmentData?.is_voided === true || 
                                 shipmentData?.status === "Voided";

                if (url && url !== "" && !isVoided) {
                    if (!labels.includes(url)) {
                        labels.push(url);
                    }
                }
            });

            // 4. Trigger Printing via your <Printer /> component props
            if (labels.length === 1) {
                setPrintUrl(labels[0]); 
                setIsPrinted(false); 
                dispatch(clearFulfillmentPrintDetails()); // Clean up loading states immediately
            }else if (labels.length > 1) {
                dispatch(printBulkLabels(labels, currentUserId));
            } else {
                dispatch(clearFulfillmentPrintDetails());
            }
        }
    }, [fulfillmentsPrintState.data, fulfillmentsPrintState.loading, dispatch]);
    useEffect(() => {
        if (orders.bulkLabel && orders.bulkLabel.filename && orders.bulkLabel.filename !== "" && orders.bulkLabel.is_shown === false) {
            setPrintUrl(process.env.REACT_APP_LABELINGCHIMP_LABEL_API_DOMAIN + "/merge-pdf?filename=" + orders.bulkLabel.filename)
            setIsPrinted(false)
        }
    }, [orders.bulkLabel])
    const onSortChange = (currentSorting) => {
        setMeta({
            ...meta,
            sorting: JSON.stringify(currentSorting)
        });
    };

    const onChangePerPage = (event) => {
        setMeta({
            ...meta,
            page: 1,
            per_page: event.target.value
        });
    };

    const onChangePagination = (event, page) => {
        setMeta({
            ...meta,
            page: page
        });
    };

    const handleFilterToggle = () => {
        setShowFilter(!showFilter);
    };

    const handleResetFilters = () => {
        setMeta({
            page: 1,
            per_page: 10,
            start_date: new Date(new Date().setDate(new Date().getDate() - 30)).toISOString().split('T')[0],
            end_date: new Date().toISOString().split('T')[0],
            sorting: JSON.stringify([{ field: 'log_date', sort: 'desc' }])
        });
    };

    const columns = [
        {
            field: 'raw_date',
            headerName: 'Date',
            headerClassName: 'super-app-theme--header',
            minWidth: 140,
            sortable: false,
            flex: 1,
            renderCell: (params) => {
                if (!params.value) return "-";
                
                // Parse '2026-06-30' string context smoothly
                const dateParts = params.value.split('-');
                if (dateParts.length !== 3) return params.value;
                
                // Construct a localized presentation date object instance safely
                const localDate = new Date(dateParts[0], dateParts[1] - 1, dateParts[2]);
                
                // Formats directly into 'Jun 30' profile format right on the client side
                const formatted = localDate.toLocaleDateString('en-US', {
                    month: 'short',
                    day: '2-digit'
                });
                
                return (
                    <Typography variant="body2" sx={{ fontWeight: 700, color: '#0F172A' }}>
                        {formatted}
                    </Typography>
                );
            }
        },
        {
            field: 'orders_count',
            headerName: 'Orders',
            headerClassName: 'super-app-theme--header',
            minWidth: 120,
            sortable: false,
            align: 'center',
            headerAlign: 'center',
            flex: 1,
            renderCell: (params) => (
                <Typography variant="body2" sx={{ fontWeight: 600, color: '#334155' }}>
                    {params.value}
                </Typography>
            )
        },
        {
            field: 'labels_count',
            headerName: 'Labels',
            headerClassName: 'super-app-theme--header',
            minWidth: 120,
            sortable: false,
            align: 'center',
            headerAlign: 'center',
            flex: 1,
            renderCell: (params) => (
                <Typography variant="body2" sx={{ fontWeight: 600, color: '#1E3A8A' }}>
                    {params.value}
                </Typography>
            )
        },
        {
            field: 'packed_count',
            headerName: 'Packed',
            headerClassName: 'super-app-theme--header',
            minWidth: 120,
            sortable: false,
            align: 'center',
            headerAlign: 'center',
            flex: 1,
            renderCell: (params) => (
                <Typography variant="body2" sx={{ fontWeight: 700, color: '#16A34A' }}>
                    {params.value}
                </Typography>
            )
        },
        {
            field: 'units_shipped_count',
            headerName: 'Units Shipped',
            headerClassName: 'super-app-theme--header',
            minWidth: 140,
            sortable: false,
            align: 'center',
            headerAlign: 'center',
            flex: 1,
            renderCell: (params) => (
                <Typography variant="body2" sx={{ fontWeight: 700, color: '#7C3AED' }}>
                    {params.value}
                </Typography>
            )
        },
        {
            field: 'action',
            headerName: 'Action',
            minWidth: 240,
            sortable: false,
            flex: 1,
            headerClassName: 'super-app-theme--header',
            renderCell: (params) => (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, height: '100%' }}>
                    <Button 
                        variant="outlined" 
                        size="small"
                        startIcon={<FileDownloadIcon sx={{ fontSize: '14px' }} />}
                        onClick={() => dispatch(exportFulfillmentBatchCSV(currentUserId, params.row.raw_date))}
                        sx={{ 
                            textTransform: 'none', borderRadius: '8px', fontWeight: 700, fontSize: '12px', height: '30px',
                            borderColor: '#E2E8F0', color: '#475569', '&:hover': { backgroundColor: '#F8FAFC', borderColor: '#CBD5E1' }
                        }}
                    >
                        Download
                    </Button>
                    <Button 
                        variant="outlined" 
                        size="small"
                        startIcon={<LocalPrintshopIcon sx={{ fontSize: '14px' }} />}
                        onClick={() => {
                            // Reset local component states before fetching a fresh payload batch
                            setPrintUrl(""); 
                            setIsPrinted(true);
                            
                            // Load the raw collection documents array directly into Redux tracking state variables
                            dispatch(loadFulfillmentBatchForPrinting(currentUserId, params.row.raw_date));
                        }}
                        sx={{ 
                            textTransform: 'none', borderRadius: '8px', fontWeight: 700, fontSize: '12px', height: '30px',
                            borderColor: '#1976d2', color: '#1976d2', '&:hover': { backgroundColor: 'rgba(25, 118, 210, 0.04)' }
                        }}
                    >
                        Print
                    </Button>
                </Box>
            )
        }
    ];

    function CustomTotalRows() {
        // ─── EXTRACTS THE ACTIVE ROWS ARRAY LENGTH INSTEAD OF THE PARENT METADATA OBJECT ───
        const rowsArray = Array.isArray(fulfillments?.data) 
            ? fulfillments.data 
            : (fulfillments?.data?.data || []);
            
        const currentCount = rowsArray.length || 0; 
        
        // Target the correct metadata location wrapper
        const totalCount = fulfillments?.meta?.total || fulfillments?.data?.total || 0; 

        return (
            <Box 
                className="custom-total-rows" 
                sx={{ 
                    display: 'inline-flex',  
                    alignItems: 'center', 
                    py: 0.6, 
                    px: 1.5, 
                    borderRadius: '8px', 
                    backgroundColor: '#F1F5F9',  
                    border: '1px solid #E2E8F0' 
                }}
            >
                <Typography variant="body2" sx={{ fontSize: '12.5px', fontWeight: 600, color: '#475569' }}> 
                    Showing <strong style={{ color: '#0F172A', fontWeight: 700 }}>{currentCount}</strong> of <strong style={{ color: '#0F172A', fontWeight: 700 }}>{totalCount}</strong> 
                </Typography>
            </Box>
        );
    }

    function CustomPagesizeoptions() {
        return (
            <Box className="custom-page-size-options" sx={{ display: 'inline-flex', alignItems: 'center' }}>
                <Stack direction="row" spacing={1.2} alignItems="center">
                    <Typography sx={{ fontSize: '13px', fontWeight: 600, color: '#64748B' }}>Show</Typography>
                    <FormControl size="small" variant="outlined" sx={{ minWidth: 80, ...enterpriseControlInputStyles }}>
                        <Select value={meta?.per_page || 10} onChange={onChangePerPage}>
                            <MenuItem value={10}>10</MenuItem>
                            <MenuItem value={20}>20</MenuItem>
                            <MenuItem value={50}>50</MenuItem>
                            <MenuItem value={100}>100</MenuItem>
                        </Select>
                    </FormControl>
                    <Typography sx={{ fontSize: '13px', fontWeight: 600, color: '#64748B' }}>entries</Typography>
                </Stack>
            </Box>
        );
    }

    function CustomToolbar() {
        return (
            <GridToolbarContainer sx={{ p: 2, bgcolor: '#F8FAFC', borderBottom: '1px solid #E2E8F0', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2, width: '100%', boxSizing: 'border-box' }}>
                <CustomPagesizeoptions />
                <CustomTotalRows />
            </GridToolbarContainer>
        );
    }

    function CustomPagination() {

        return (
            <Box
                sx={{
                    p: 1.5,
                    bgcolor: '#F8FAFC', 
                    borderTop: '1px solid #E2E8F0', 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'flex-end', 
                    
                    width: '100%', 
                    boxSizing: 'border-box', 
                    '& .MuiPaginationItem-root': {
                        fontSize: '13px', 
                        fontWeight: 600, 
                        borderRadius: '8px', 
                        '&.Mui-selected': {
                            backgroundColor: '#1976d2', 
                            color: '#FFFFFF', 
                            '&:hover': { backgroundColor: '#115293' } 
                        }
                    }
                }}
            >
                <Pagination
                    color="primary" 
                    shape="circular" 
                    page={fulfillments && fulfillments.data && fulfillments.data.meta && fulfillments.data.meta.current_page} 
                    count={fulfillments && fulfillments.data && fulfillments.data.meta && fulfillments.data.meta.last_page} 
                    onChange={onChangePagination} 
                />
            </Box>
        );
    }

    return (
        <Box sx={{ width: '100%', boxSizing: 'border-box'}}>
            <Box className="sccardhead" sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 2.5, flexWrap: 'wrap', gap: 2 }}>
                <Stack direction="row" alignItems="center" spacing={2} sx={{ flexWrap: 'wrap', gap: 1.5 }}>
                    <Typography variant="h5" component="h1" sx={{ fontWeight: 800, color: '#0F172A', letterSpacing: '-0.5px', margin: 0 }}>
                        Fulfillments Report
                    </Typography>
                </Stack>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2, p: 0, m: 0 }}>
                    <Tooltip title="Filter Panels" placement="top">
                        <IconButton sx={utilityIconStyles(showFilter)} onClick={handleFilterToggle}>
                            <FilterAltOutlinedIcon sx={{ fontSize: '20px' }} />
                        </IconButton>
                    </Tooltip>
                </Box>
            </Box>

            <FulfillmentsLogFilters 
                showFilter={showFilter} 
                meta={meta} 
                onFilterChange={(updated) => setMeta(prev => ({ ...prev, ...updated }))} 
                onReset={handleResetFilters} 
            />
            <Printer isPrinted={isPrinted} setIsPrinted={setIsPrinted} print_url={printUrl} />    
            <Card sx={{ width: '100%', backgroundColor: '#FFFFFF', border: '1px solid #E2E8F0', borderRadius: '16px', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05), 0 2px 4px -1px rgba(0, 0, 0, 0.02)', overflow: 'hidden' }}>
                <Box sx={{ width: '100%', backgroundColor: '#FFFFFF' }}>
                    <DataGrid
                        sx={{
                            border: 'none',
                            backgroundColor: '#FFFFFF',
                            overflow: 'hidden',
                            '& .MuiDataGrid-topContainer': { backgroundColor: '#F8FAFC', '&::after': { display: 'none' } },
                            '& .MuiDataGrid-columnHeaders': { backgroundColor: '#F8FAFC', borderBottom: '1px solid #E2E8F0', minHeight: '52px !important', maxHeight: '52px !important' },
                            '& .MuiDataGrid-columnHeader': { color: '#475569', fontWeight: 700, fontSize: '13px', '&:focus, &:focus-within': { outline: 'none' } },
                            '& .MuiDataGrid-row': { transition: 'background-color 0.15s ease-in-out', borderBottom: '1px solid #F1F5F9', alignItems: 'center', '&:hover': { backgroundColor: '#F8FAFC' }, '&.Mui-selected': { backgroundColor: 'rgba(25, 118, 210, 0.04)', '&:hover': { backgroundColor: 'rgba(25, 118, 210, 0.08)' } } },
                            '& .MuiDataGrid-cell': { fontSize: '13.5px', color: '#334155', borderBottom: 'none', display: 'flex', alignItems: 'center', py: '12px', '&:focus, &:focus-within': { outline: 'none' } },
                            '& .MuiLinearProgress-root': { height: '3px', backgroundColor: '#E2E8F0', '& .MuiLinearProgress-bar': { backgroundColor: '#1976d2' } }
                        }}
                        getRowId={(row) => row.raw_date}
                        
                        // ─── CHANGE THIS LINE TO EXTRACT THE INNER ROW ARRAY ───
                        rows={(fulfillments?.data && Array.isArray(fulfillments.data)) ? fulfillments.data : (fulfillments?.data?.data || [])}
                        
                        columns={columns}
                        autoHeight={true}
                        disableColumnFilter={true}
                        disableColumnMenu={true}
                        sortingMode="server"
                        onSortModelChange={(newSortModel) => onSortChange(newSortModel)}
                        sortModel={meta.sorting ? JSON.parse(meta.sorting) : []}
                        disableRowSelectionOnClick
                        slots={{
                            loadingOverlay: LinearProgress,
                            footer: CustomPagination,
                            toolbar: CustomToolbar,
                            NorowsData
                        }}
                        loading={fulfillments?.loading}
                    />
                </Box>
            </Card>
        </Box>
    );
};

export default FulfillmentsReportPage;